import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, TextField, Button, Paper, InputAdornment, Snackbar, CircularProgress } from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MuiAlert from '@mui/material/Alert';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../Header';
import { useRecommendationEngineAuth } from './RecommendationEngineAuthProvider';
import axios from 'axios';
import { endpoint } from '../../resource/Constant';
import { parseEmail } from '../../resource/Constant';
import { useNavigate } from 'react-router-dom';

const EmailVerificationPage = () => {
  const [email, setEmail] = useState('');
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState(['', '', '', '']);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [loading, setLoading] = useState(false);
  const { recomengineLogin } = useRecommendationEngineAuth();
  const navigate = useNavigate();

  const otpRefs = useRef([]);

  useEffect(() => {
    let timer;
    if (otpSent && resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [otpSent, resendTimer]);

  const handleOtpChange = (index, value) => {
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // Ensure only one digit
    setOtp(newOtp);
    if (value && index < otpRefs.current.length - 1) {
      otpRefs.current[index + 1].focus();
    }
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email) return showSnackbar('Email is required', 'warning');
    setLoading(true);

    if (email === parseEmail) {
      axios.post(endpoint + "/api/recommendation/parse" , {} , {params : {email}})
      .then((response) => {
      localStorage.setItem('recommendationenginetoken', response?.data?.jwt);
      recomengineLogin();
      navigate('/recommendation/engine');
      })
      .catch((error) => {
        showSnackbar(error?.response?.data?.detail || 'OTP verification failed', 'error');
        setLoading(false);
      })
    }
    else {
      axios
      .post(`${endpoint}/api/recommendation/sendotp`, {}, { params: { email } })
      .then((res) => {
        setLoading(false);
        setStep(2);
        setOtpSent(true);
        setResendTimer(30);
        showSnackbar(`Verification code send to ${email}`, 'info');
      })
      .catch((error) => {
        setLoading(false);
        showSnackbar(error?.response?.data?.message || 'Failed to send OTP', 'error');
      });
    }    
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    const otpValue = otp.join('');
    if (otpValue.length < 4) return showSnackbar('Please enter complete OTP', 'warning');

    setLoading(true);
    try {
      const response = await axios.post(
        `${endpoint}/api/recommendation/verifyotp`,
        {},
        {
          params: { email, otp: otpValue }
        }
      );
      localStorage.setItem('recommendationenginetoken', response?.data?.data?.token);
      recomengineLogin();
      navigate('/recommendation/engine');
    } catch (error) {
      showSnackbar(error?.response?.data?.message || 'OTP verification failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmail = () => {
    setStep(1);
    console.log('Hitting change email');
    console.log(step);
  };

  const Alert = React.forwardRef((props, ref) => <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />);

  return (
    <>
      <Header />
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{
          background: 'linear-gradient(to bottom right, #f3faff, #ffffff)',
          fontFamily: 'Inter, sans-serif',
          overflow: 'hidden'
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={step} 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.4 }}
          >
            <Paper elevation={6} sx={{ p: 4, borderRadius: 4, width: 500, textAlign: 'center' }}>
              <Box
                sx={{
                  bgcolor: '#e3edff',
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  mx: 'auto',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <EmailOutlinedIcon sx={{ fontSize: 30, color: '#5387f5' }} />
              </Box>
              <Typography variant="h6" fontWeight="bold" mb={1}>
                Email Verification
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                {step === 1
                  ? 'Enter your email to receive an OTP for verification.'
                  : "Enter the OTP sent to your email to proceed. (Check your spam folder if you don't see it.)"}
              </Typography>

              <TextField
                fullWidth
                variant="outlined"
                label="Enter your email"
                value={email}
                disabled={step === 2}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSendOtp(e);
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon />
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 3 }}
              />

              {step === 1 ? (
                <motion.div whileTap={{ scale: 0.98 }} whileHover={{ scale: 1.01 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSendOtp}
                    disabled={loading}
                    sx={{
                      backgroundColor: '#96b8f8',
                      '&:hover': { backgroundColor: '#84a9f5' }
                    }}
                    startIcon={loading && <CircularProgress size={18} />}
                  >
                    {loading ? 'Sending...' : 'SEND OTP'}
                  </Button>
                </motion.div>
              ) : (
                <>
                  <Box display="flex" justifyContent="center" gap={2} mb={3}>
                    {otp.map((digit, index) => (
                      <TextField
                        key={index}
                        inputRef={(el) => (otpRefs.current[index] = el)}
                        inputProps={{
                          maxLength: 1,
                          style: { textAlign: 'center' }
                        }}
                        value={digit}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/, '');
                          handleOtpChange(index, value);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Backspace') {
                            if (otp[index] === '' && index > 0) {
                              otpRefs.current[index - 1]?.focus();
                            } else {
                              const newOtp = [...otp];
                              newOtp[index] = '';
                              setOtp(newOtp);
                            }
                          } else if (e.key === 'ArrowLeft' && index > 0) {
                            otpRefs.current[index - 1]?.focus();
                          } else if (e.key === 'ArrowRight' && index < otp.length - 1) {
                            otpRefs.current[index + 1]?.focus();
                          } else if (e.key === 'Enter') {
                            if (otp.every((digit) => digit !== '')) {
                              handleSignIn(e);
                            }
                          }
                        }}
                        sx={{ width: 50 }}
                      />
                    ))}
                  </Box>

                  <Button
                    onClick={handleSignIn}
                    fullWidth
                    variant="contained"
                    disabled={loading}
                    sx={{
                      backgroundColor: '#96b8f8',
                      '&:hover': { backgroundColor: '#84a9f5' }
                    }}
                    startIcon={loading && <CircularProgress size={18} />}
                  >
                    {loading ? 'Verifying...' : 'SIGN IN'}
                  </Button>

                  {resendTimer > 0 ? (
                    <>
                      <Typography variant="caption" display="block" mt={2} color="text.secondary">
                        ⏳ Resend OTP in {resendTimer}s
                      </Typography>
                      <Button size="small" sx={{marginTop : 1}} variant={'outlined'} onClick={handleChangeEmail}>
                        Change Email
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button size="small" onClick={handleSendOtp}>
                        Resend OTP
                      </Button>
                      <Button size="small" onClick={handleChangeEmail}>
                        Change Email
                      </Button>
                    </>
                  )}

                  <Box mt={3} display="flex" alignItems="center" justifyContent="center" bgcolor="#e6f8ea" borderRadius={2} py={1} px={2}>
                    <CheckCircleOutlineIcon fontSize="small" color="success" />
                    <Typography variant="body2" ml={1} sx={{ color: 'green', fontWeight: 500 }}>
                      OTP sent to {email}
                    </Typography>
                  </Box>
                </>
              )}
            </Paper>
          </motion.div>
        </AnimatePresence>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default EmailVerificationPage;
